import {Trans} from '@lingui/macro';
import React, {useCallback, useState} from 'react';

import {useAppDispatch} from '../../../helpers/reduxHelpers';
import {
  navigateToLinkAction,
  NavigationForbiddenError,
  NavigationNotFoundError,
} from '../../../store/stateless/navigationActions';
import {OpenConversationDialog} from '../../inboxes/openConversationDialog';

interface LinkError {
  link: string;
  error: unknown;
}

/**
 * Provides a function to navigate to a link and a component to be rendered
 * in case there was an error with that link (404, 403, etc).
 */
export function useLinkNavigation() {
  const dispatch = useAppDispatch();
  const [linkError, setLinkError] = useState<LinkError | undefined>(undefined);

  const navigateToLink = useCallback(
    async (link: string, shiftKey?: boolean) => {
      try {
        resetLinkError();
        return await dispatch(navigateToLinkAction(link, shiftKey));
      } catch (error) {
        setLinkError({
          link,
          error,
        });
        return false;
      }
    },
    [dispatch],
  );

  const resetLinkError = () => {
    setLinkError(undefined);
  };

  const maybeRenderLinkError = () => {
    if (!linkError) {
      return null;
    }

    const errorMessage = getLinkErrorMessage(linkError.error);
    return (
      <OpenConversationDialog
        hasError
        initialValue={linkError.link}
        errorMessage={errorMessage}
        onValueSelect={navigateToLink}
        onRequestClose={resetLinkError}
      />
    );
  };

  return {navigateToLink, maybeRenderLinkError, linkError, resetLinkError};
}

export type NavigateToLinkMethod = (link: string, shiftKey?: boolean | undefined) => Promise<boolean>;

export function getLinkErrorMessage(error: unknown) {
  if (error instanceof NavigationNotFoundError) {
    return <Trans id="link-navigation-error-not-found">This conversation was not found</Trans>;
  }

  if (error instanceof NavigationForbiddenError) {
    return <Trans id="link-navigation-error-forbidden">You do not have access to this conversation</Trans>;
  }

  return (
    <Trans id="link-navigation-error-default">
      There was an unexpected error trying to navigate to this link
    </Trans>
  );
}
